import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { 
  Dna,
  Home,
  Flame,
  Droplet,
  Grid3X3,
  Square,
  Shield,
  RectangleHorizontal,
  Waves,
  Trees,
  Fence,
  Warehouse,
  ChevronRight,
  Check,
  Loader2,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  X,
  Zap,
  Compass,
  Building2,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  DollarSign,
  Clock,
  XCircle,
  Info,
  Camera,
  Image,
  Upload
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping for components
const ICONS = {
  home: Home,
  flame: Flame,
  droplet: Droplet,
  'grid-3x3': Grid3X3,
  square: Square,
  shield: Shield,
  'rectangle-horizontal': RectangleHorizontal,
  waves: Waves,
  trees: Trees,
  fence: Fence,
  warehouse: Warehouse
};

// Compass direction icons
const COMPASS_ICONS = {
  north: ArrowUp,
  south: ArrowDown,
  east: ArrowRight,
  west: ArrowLeft,
  front: ArrowUp,
  rear: ArrowDown,
  left: ArrowLeft,
  right: ArrowRight
};

// DNA Options
const WALL_TYPES = [
  { value: 'brick', label: 'Brick' },
  { value: 'render', label: 'Render' },
  { value: 'stone', label: 'Stone' },
  { value: 'timber', label: 'Timber Frame' }
];

const ROOF_TYPES = [
  { value: 'hipped', label: 'Hipped' },
  { value: 'gabled', label: 'Gabled' },
  { value: 'flat', label: 'Flat' }
];

const PROPERTY_ERAS = [
  { value: 'pre_1919', label: 'Pre-1919 (Victorian/Edwardian)' },
  { value: 'inter_war', label: 'Inter-war (1919-1945)' },
  { value: 'post_war', label: 'Post-war (1945-1980)' },
  { value: 'modern', label: 'Modern (1980+)' }
];

// Severity colors with budget tiers
const SEVERITY_CONFIG = {
  green: { 
    bg: 'bg-green-500', 
    hover: 'hover:bg-green-600', 
    text: 'text-green-500', 
    border: 'border-green-500',
    label: 'Minor',
    tier: 'Maintenance',
    icon: CheckCircle2
  },
  yellow: { 
    bg: 'bg-yellow-500', 
    hover: 'hover:bg-yellow-600', 
    text: 'text-yellow-500', 
    border: 'border-yellow-500',
    label: 'Moderate',
    tier: 'Planned (12mo)',
    icon: AlertTriangle
  },
  orange: { 
    bg: 'bg-orange-500', 
    hover: 'hover:bg-orange-600', 
    text: 'text-orange-500', 
    border: 'border-orange-500',
    label: 'Moderate-High',
    tier: 'Priority (6mo)',
    icon: AlertCircle
  },
  red: { 
    bg: 'bg-red-500', 
    hover: 'hover:bg-red-600', 
    text: 'text-red-500', 
    border: 'border-red-500',
    label: 'Major',
    tier: 'Urgent/Safety',
    icon: XCircle
  }
};

export default function EnvelopeEngineDialog({ open, onClose, inspection, onSuccess }) {
  const { token } = useContext(AuthContext);
  const [step, setStep] = useState('dna'); // dna, inspect, summary
  const [loading, setLoading] = useState(false);
  
  // DNA State
  const [dna, setDna] = useState({
    primary_wall_type: 'brick',
    primary_roof_type: 'hipped',
    property_era: 'modern'
  });
  
  // Configuration from backend
  const [config, setConfig] = useState(null);
  const [eraInfo, setEraInfo] = useState(null);
  const [materialExclusions, setMaterialExclusions] = useState(null);
  
  // Envelope inspection state
  const [envelopeId, setEnvelopeId] = useState(null);
  const [fixCards, setFixCards] = useState([]);
  const [autoTriggered, setAutoTriggered] = useState([]);
  const [crossTriggerAlerts, setCrossTriggerAlerts] = useState([]);
  
  // Budget totals
  const [budgetSummary, setBudgetSummary] = useState({ total_low: 0, total_high: 0 });
  
  // COMPASS TOGGLE STATE (Contextual Photo Anchor)
  const [currentFacade, setCurrentFacade] = useState(null);
  const [currentLevel, setCurrentLevel] = useState(null);
  
  // 3-Tap State
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [availableIssues, setAvailableIssues] = useState([]);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [selectedSeverity, setSelectedSeverity] = useState(null);
  const [findingNotes, setFindingNotes] = useState('');
  
  // Photo Capture State
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  // Configure envelope when DNA changes
  const configureEnvelope = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/envelope/configure`, dna, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data.components);
      setEraInfo(response.data.era_info);
      setMaterialExclusions(response.data.material_exclusions);
      
      // Create envelope inspection
      const createResponse = await axios.post(`${API}/envelope/inspection`, {
        inspection_id: inspection.id,
        dna
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEnvelopeId(createResponse.data.id);
      setStep('inspect');
      toast.success('Envelope Engine configured - Begin walkaround');
    } catch (error) {
      toast.error('Failed to configure envelope');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Set spatial context (Compass Toggle)
  const setSpatialContext = async (facade, level) => {
    if (!envelopeId) return;
    
    try {
      await axios.post(`${API}/envelope/inspection/${envelopeId}/set-spatial`, null, {
        params: { facade, level },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentFacade(facade);
      setCurrentLevel(level);
      
      const parts = [];
      if (facade) parts.push(facade.replace('_', ' ').toUpperCase());
      if (level) parts.push(level.replace('_', ' ').toUpperCase());
      
      toast.success(`Spatial context: ${parts.join(' / ') || 'Cleared'}`);
    } catch (error) {
      console.error('Failed to set spatial context:', error);
    }
  };

  // Handle component selection (Tap 1)
  const selectComponent = (componentKey) => {
    setSelectedComponent(componentKey);
    setSelectedIssue(null);
    setSelectedSeverity(null);
    setFindingNotes('');
    
    const comp = config[componentKey];
    setSelectedTrigger(comp.default_trigger);
    setAvailableIssues(comp.available_issues);
  };

  // Handle trigger change
  const changeTrigger = async (trigger) => {
    setSelectedTrigger(trigger);
    setSelectedIssue(null);
    setSelectedSeverity(null);
    
    try {
      const response = await axios.get(`${API}/envelope/component/${selectedComponent}/issues`, {
        params: { trigger, wall_type: dna.primary_wall_type },
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableIssues(response.data.issues);
    } catch (error) {
      console.error('Failed to load issues:', error);
    }
  };

  // Handle issue selection (Tap 2)
  const selectIssue = (issue) => {
    setSelectedIssue(issue);
    setSelectedSeverity(null);
  };

  // Photo Upload Handler
  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingPhoto(true);
    
    for (const file of files) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`Invalid file type: ${file.name}`);
        continue;
      }
      
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File too large: ${file.name} (max 10MB)`);
        continue;
      }
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/uploads/photo`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        // Add to pending photos with spatial context
        setPendingPhotos(prev => [...prev, {
          url: response.data.url,
          filename: response.data.filename,
          facade: currentFacade,
          level: currentLevel,
          timestamp: new Date().toISOString()
        }]);
        
        toast.success(`Photo uploaded: ${file.name}`);
      } catch (error) {
        toast.error(`Failed to upload: ${file.name}`);
        console.error(error);
      }
    }
    
    setUploadingPhoto(false);
    // Reset file input
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  // Remove pending photo
  const removePendingPhoto = async (index) => {
    const photo = pendingPhotos[index];
    try {
      await axios.delete(`${API}/uploads/photo/${photo.filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to delete photo:', error);
    }
    setPendingPhotos(prev => prev.filter((_, i) => i !== index));
  };

  // Handle severity selection (Tap 3) - This adds the fix card with photos
  const selectSeverity = async (severity) => {
    setSelectedSeverity(severity);
    
    try {
      // Build photos array with full URLs
      const photoUrls = pendingPhotos.map(p => `${BACKEND_URL}${p.url}`);
      
      const response = await axios.post(`${API}/envelope/inspection/${envelopeId}/quick-add`, null, {
        params: {
          component: selectedComponent,
          issue_code: selectedIssue.code,
          severity,
          facade: currentFacade,
          level: currentLevel,
          notes: findingNotes || null,
          photos: photoUrls.length > 0 ? photoUrls.join(',') : null
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFixCards(prev => [...prev, response.data.fix_card]);
      setBudgetSummary(response.data.budget_summary);
      
      // Handle cross-component triggers
      if (response.data.cross_trigger_alerts?.length > 0) {
        setCrossTriggerAlerts(prev => [...prev, ...response.data.cross_trigger_alerts]);
        
        // Show cross-trigger alert
        response.data.cross_trigger_alerts.forEach(alert => {
          toast.warning(
            <div>
              <p className="font-medium">Cross-Component Alert</p>
              <p className="text-sm">{alert.alert_message}</p>
            </div>,
            { duration: 5000 }
          );
        });
      }
      
      if (response.data.auto_triggered?.length > 0) {
        setAutoTriggered(prev => [...new Set([...prev, ...response.data.auto_triggered])]);
      }
      
      const sevConfig = SEVERITY_CONFIG[severity];
      const photoCount = pendingPhotos.length;
      toast.success(
        <div>
          <p className="font-medium">{config[selectedComponent]?.name} - {selectedIssue.label}</p>
          <p className="text-sm">{sevConfig.label} ({sevConfig.tier})</p>
          {currentFacade && <p className="text-xs opacity-75">📍 {currentFacade.toUpperCase()} Facade</p>}
          {photoCount > 0 && <p className="text-xs opacity-75">📷 {photoCount} photo{photoCount > 1 ? 's' : ''} attached</p>}
        </div>
      );
      
      // Reset for next entry
      setSelectedIssue(null);
      setSelectedSeverity(null);
      setFindingNotes('');
      setPendingPhotos([]); // Clear pending photos
    } catch (error) {
      toast.error('Failed to add fix card');
      console.error(error);
    }
  };

  // Remove fix card
  const removeFixCard = async (cardId) => {
    try {
      const response = await axios.delete(`${API}/envelope/inspection/${envelopeId}/fix-card/${cardId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFixCards(prev => prev.filter(c => c.id !== cardId));
      setBudgetSummary(response.data.budget_summary);
      toast.success('Finding removed');
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  // Acknowledge cross-trigger alert
  const acknowledgeAlert = async (index) => {
    try {
      await axios.post(`${API}/envelope/inspection/${envelopeId}/acknowledge-alert/${index}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCrossTriggerAlerts(prev => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
    }
  };

  // Get summary and finish
  const finishInspection = async () => {
    try {
      const response = await axios.get(`${API}/envelope/inspection/${envelopeId}/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Inspection complete: ${fixCards.length} findings, £${response.data.budget_summary.total_low.toLocaleString()} - £${response.data.budget_summary.total_high.toLocaleString()}`);
      onSuccess?.({
        envelope_id: envelopeId,
        dna,
        fix_cards: fixCards,
        summary: response.data
      });
      onClose();
    } catch (error) {
      toast.error('Failed to complete inspection');
    }
  };

  const getComponentIcon = (iconName) => {
    const IconComponent = ICONS[iconName] || Home;
    return <IconComponent className="h-5 w-5" />;
  };

  // Format currency
  const formatCurrency = (low, high) => {
    if (low === 0 && high === 0) return 'TBC';
    return `£${low.toLocaleString()} - £${high.toLocaleString()}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-hidden flex flex-col" data-testid="envelope-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Dna className="h-5 w-5 text-accent" />
            Envelope Engine
            {step === 'inspect' && (
              <Badge variant="outline" className="ml-2">
                {fixCards.length} findings
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {inspection?.property_address}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {/* STEP 1: DNA Configuration */}
          {step === 'dna' && (
            <div className="space-y-6 p-4">
              <Card className="border-2 border-dashed border-accent/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Dna className="h-5 w-5 text-accent" />
                    Global Header - The "DNA"
                  </CardTitle>
                  <CardDescription>
                    Set these three toggles to prime the engine. The Material Integrity Validator will filter impossible findings based on your selections.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Wall Type */}
                    <div>
                      <Label className="text-base font-semibold">Primary Wall Type</Label>
                      <Select
                        value={dna.primary_wall_type}
                        onValueChange={(v) => setDna({...dna, primary_wall_type: v})}
                      >
                        <SelectTrigger className="mt-2" data-testid="wall-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {WALL_TYPES.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {dna.primary_wall_type === 'timber' && (
                        <p className="text-xs text-muted-foreground mt-2">
                          ℹ️ Timber frame selected - brick-specific issues will be replaced with timber defects
                        </p>
                      )}
                    </div>

                    {/* Roof Type */}
                    <div>
                      <Label className="text-base font-semibold">Primary Roof Type</Label>
                      <Select
                        value={dna.primary_roof_type}
                        onValueChange={(v) => setDna({...dna, primary_roof_type: v})}
                      >
                        <SelectTrigger className="mt-2" data-testid="roof-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROOF_TYPES.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Property Era */}
                    <div>
                      <Label className="text-base font-semibold">Property Era</Label>
                      <Select
                        value={dna.property_era}
                        onValueChange={(v) => setDna({...dna, property_era: v})}
                      >
                        <SelectTrigger className="mt-2" data-testid="era-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PROPERTY_ERAS.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Era Info Preview */}
                  {dna.property_era && (
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm font-medium mb-2">Era Considerations:</p>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        {dna.property_era === 'pre_1919' && (
                          <>
                            <li>• Likely solid wall construction - check for damp</li>
                            <li>• Original timber joinery - check for decay</li>
                            <li>• May have no DPC or slate DPC</li>
                          </>
                        )}
                        {dna.property_era === 'inter_war' && (
                          <>
                            <li>• Early cavity walls - potential wall tie issues</li>
                            <li>• Softwood joinery - check paint condition</li>
                            <li>• Physical DPC - check for bridging</li>
                          </>
                        )}
                        {dna.property_era === 'post_war' && (
                          <>
                            <li>• Check for non-traditional construction</li>
                            <li>• May have original single glazing</li>
                            <li>• Physical or bitumen DPC</li>
                          </>
                        )}
                        {dna.property_era === 'modern' && (
                          <>
                            <li>• Check cavity width and insulation</li>
                            <li>• Likely uPVC windows - check seals</li>
                            <li>• Membrane DPC standard</li>
                          </>
                        )}
                      </ul>
                    </div>
                  )}

                  <Button 
                    onClick={configureEnvelope} 
                    disabled={loading}
                    className="w-full"
                    size="lg"
                    data-testid="configure-btn"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ChevronRight className="h-4 w-4 mr-2" />}
                    Start Walkaround Inspection
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* STEP 2: Inspection with Compass Toggle */}
          {step === 'inspect' && config && (
            <div className="flex flex-col h-full">
              {/* COMPASS TOGGLE BAR - Contextual Photo Anchor */}
              <div className="bg-muted/30 border-b p-3 flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Compass className="h-5 w-5 text-accent" />
                  <span className="text-sm font-medium">Spatial Context:</span>
                </div>
                
                {/* Facade Toggle */}
                <div className="flex items-center gap-1">
                  <Label className="text-xs text-muted-foreground mr-2">Facade:</Label>
                  <div className="flex gap-1">
                    {['north', 'south', 'east', 'west'].map(dir => {
                      const Icon = COMPASS_ICONS[dir];
                      return (
                        <Button
                          key={dir}
                          type="button"
                          variant={currentFacade === dir ? 'default' : 'outline'}
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setSpatialContext(dir, currentLevel)}
                          data-testid={`facade-${dir}`}
                        >
                          <Icon className="h-4 w-4" />
                        </Button>
                      );
                    })}
                    {currentFacade && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-xs"
                        onClick={() => setSpatialContext(null, currentLevel)}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
                
                <Separator orientation="vertical" className="h-6" />
                
                {/* Level Toggle */}
                <div className="flex items-center gap-1">
                  <Label className="text-xs text-muted-foreground mr-2">Level:</Label>
                  <Select 
                    value={currentLevel || ''} 
                    onValueChange={(v) => setSpatialContext(currentFacade, v || null)}
                  >
                    <SelectTrigger className="h-8 w-28" data-testid="level-select">
                      <SelectValue placeholder="Level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ground">Ground</SelectItem>
                      <SelectItem value="first">First</SelectItem>
                      <SelectItem value="second">Second</SelectItem>
                      <SelectItem value="roof">Roof</SelectItem>
                      <SelectItem value="basement">Basement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Current Context Display */}
                {(currentFacade || currentLevel) && (
                  <Badge variant="secondary" className="ml-auto">
                    📍 {[currentFacade?.toUpperCase(), currentLevel?.replace('_', ' ').toUpperCase()].filter(Boolean).join(' / ')}
                  </Badge>
                )}
              </div>

              {/* Cross-Component Alerts */}
              {crossTriggerAlerts.length > 0 && (
                <div className="bg-amber-500/10 border-b border-amber-500/30 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    <span className="text-sm font-medium text-amber-700">Cross-Component Alerts</span>
                  </div>
                  <div className="space-y-2">
                    {crossTriggerAlerts.map((alert, index) => (
                      <div key={index} className="flex items-start gap-2 bg-white/50 rounded p-2">
                        <Info className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{alert.alert_message}</p>
                          <p className="text-xs text-muted-foreground">{alert.reason}</p>
                          <p className="text-xs mt-1">
                            <span className="font-medium">Check:</span> {alert.target_component.replace('_', ' ')} for{' '}
                            {alert.suggested_issues.join(', ')}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => acknowledgeAlert(index)}
                        >
                          <Check className="h-3 w-3 mr-1" /> Got it
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Main 3-Tap Interface */}
              <div className="flex gap-4 p-4 flex-1 min-h-0">
                {/* Left: Component Selection (Tap 1) */}
                <div className="w-1/4 flex flex-col">
                  <Label className="text-base font-semibold mb-2">1. Component</Label>
                  <div className="space-y-2 overflow-y-auto flex-1">
                    {Object.entries(config).map(([key, comp]) => {
                      const count = fixCards.filter(c => c.component === key).length;
                      const isAutoTriggered = autoTriggered.includes(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => selectComponent(key)}
                          className={`w-full p-3 text-left rounded-lg border transition-all ${
                            selectedComponent === key 
                              ? 'border-accent bg-accent/10 ring-2 ring-accent' 
                              : isAutoTriggered
                                ? 'border-amber-500 bg-amber-50'
                                : 'border-border hover:border-accent/50'
                          }`}
                          data-testid={`component-${key}`}
                        >
                          <div className="flex items-center gap-2">
                            {getComponentIcon(comp.icon)}
                            <span className="font-medium text-sm">{comp.name}</span>
                            {count > 0 && (
                              <Badge variant="secondary" className="ml-auto">
                                {count}
                              </Badge>
                            )}
                            {isAutoTriggered && count === 0 && (
                              <Badge variant="outline" className="ml-auto text-amber-600 border-amber-500">
                                Check
                              </Badge>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Middle: Issue Selection (Tap 2) */}
                <div className="w-1/3 flex flex-col">
                  {selectedComponent ? (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-base font-semibold">2. Issue</Label>
                        {config[selectedComponent].available_triggers.length > 1 && (
                          <Select value={selectedTrigger} onValueChange={changeTrigger}>
                            <SelectTrigger className="w-32 h-8" data-testid="trigger-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {config[selectedComponent].available_triggers.map(t => (
                                <SelectItem key={t.value} value={t.value}>
                                  {t.value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <div className="space-y-2 overflow-y-auto flex-1">
                        {availableIssues.map((issue) => (
                          <button
                            key={issue.code}
                            type="button"
                            onClick={() => selectIssue(issue)}
                            className={`w-full p-3 text-left rounded-lg border transition-all ${
                              selectedIssue?.code === issue.code 
                                ? 'border-accent bg-accent/10 ring-2 ring-accent' 
                                : 'border-border hover:border-accent/50'
                            }`}
                            data-testid={`issue-${issue.code}`}
                          >
                            <p className="font-medium text-sm">{issue.label}</p>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                              {issue.recommendation}
                            </p>
                            {issue.est_cost_high > 0 && (
                              <p className="text-xs text-accent mt-1">
                                Est: £{issue.est_cost_low} - £{issue.est_cost_high}
                              </p>
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-32 text-muted-foreground">
                      ← Select a component first
                    </div>
                  )}
                </div>

                {/* Right: Severity Selection (Tap 3) + Notes + Added Cards */}
                <div className="w-5/12 flex flex-col">
                  {selectedIssue ? (
                    <>
                      <Label className="text-base font-semibold mb-2">3. Severity (Budget Tier)</Label>
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        {Object.entries(SEVERITY_CONFIG).map(([sev, cfg]) => {
                          const Icon = cfg.icon;
                          return (
                            <button
                              key={sev}
                              type="button"
                              onClick={() => selectSeverity(sev)}
                              className={`p-3 rounded-lg border-2 transition-all text-left ${
                                selectedSeverity === sev
                                  ? `${cfg.border} ${cfg.bg} text-white`
                                  : `border-border hover:${cfg.border}`
                              }`}
                              data-testid={`severity-${sev}`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <Icon className={`h-4 w-4 ${selectedSeverity === sev ? 'text-white' : cfg.text}`} />
                                <span className="text-sm font-medium">{cfg.label}</span>
                              </div>
                              <p className={`text-xs ${selectedSeverity === sev ? 'text-white/80' : 'text-muted-foreground'}`}>
                                {cfg.tier}
                              </p>
                            </button>
                          );
                        })}
                      </div>

                      {/* Notes field */}
                      <div className="mb-3">
                        <Label className="text-xs text-muted-foreground">Notes (optional)</Label>
                        <Textarea
                          value={findingNotes}
                          onChange={(e) => setFindingNotes(e.target.value)}
                          placeholder="Add notes before selecting severity..."
                          className="h-16 text-sm resize-none mt-1"
                          data-testid="finding-notes"
                        />
                      </div>

                      {/* Photo Capture Section */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-xs text-muted-foreground flex items-center gap-1">
                            <Camera className="h-3 w-3" />
                            Photos (optional)
                          </Label>
                          {pendingPhotos.length > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {pendingPhotos.length} photo{pendingPhotos.length > 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Photo Upload Button */}
                        <input
                          type="file"
                          ref={photoInputRef}
                          accept="image/*"
                          multiple
                          onChange={handlePhotoSelect}
                          className="hidden"
                          data-testid="photo-input"
                        />
                        
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => photoInputRef.current?.click()}
                            disabled={uploadingPhoto}
                            data-testid="add-photo-btn"
                          >
                            {uploadingPhoto ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <Camera className="h-4 w-4 mr-2" />
                            )}
                            {uploadingPhoto ? 'Uploading...' : 'Add Photo'}
                          </Button>
                        </div>
                        
                        {/* Photo Thumbnails */}
                        {pendingPhotos.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {pendingPhotos.map((photo, index) => (
                              <div 
                                key={index} 
                                className="relative group w-16 h-16 rounded-md overflow-hidden border"
                              >
                                <img 
                                  src={`${BACKEND_URL}${photo.url}`} 
                                  alt={`Photo ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                <button
                                  type="button"
                                  onClick={() => removePendingPhoto(index)}
                                  className="absolute top-0 right-0 bg-destructive text-white p-0.5 rounded-bl opacity-0 group-hover:opacity-100 transition-opacity"
                                  data-testid={`remove-photo-${index}`}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                                {photo.facade && (
                                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[8px] px-1 py-0.5 text-center">
                                    {photo.facade.toUpperCase()}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Current Selection Summary */}
                      <Card className="bg-accent/5 mb-3">
                        <CardContent className="p-3">
                          <p className="text-xs text-muted-foreground">Adding:</p>
                          <p className="text-sm font-bold text-accent">
                            {config[selectedComponent]?.name}
                          </p>
                          <p className="text-sm">{selectedIssue.label}</p>
                          {currentFacade && (
                            <p className="text-xs text-muted-foreground mt-1">
                              📍 {currentFacade.toUpperCase()} Facade {currentLevel ? `/ ${currentLevel.toUpperCase()}` : ''}
                            </p>
                          )}
                          {pendingPhotos.length > 0 && (
                            <p className="text-xs text-muted-foreground">
                              📷 {pendingPhotos.length} photo{pendingPhotos.length > 1 ? 's' : ''} attached
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-20 text-muted-foreground text-sm">
                      ← Select an issue
                    </div>
                  )}

                  {/* Added Cards List */}
                  {fixCards.length > 0 && (
                    <div className="flex-1 overflow-hidden flex flex-col">
                      <Label className="text-sm font-semibold mb-2">
                        Findings ({fixCards.length})
                      </Label>
                      <div className="space-y-2 overflow-y-auto flex-1">
                        {fixCards.slice().reverse().map((card) => {
                          const sevCfg = SEVERITY_CONFIG[card.severity];
                          const photoCount = card.photos?.length || 0;
                          return (
                            <div 
                              key={card.id}
                              className="flex items-start gap-2 p-2 bg-muted/50 rounded text-sm"
                            >
                              <div className={`w-3 h-3 rounded-full ${sevCfg?.bg} mt-1 flex-shrink-0`} />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{card.issue_label}</p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {config[card.component]?.name}
                                </p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  {card.spatial_tag?.facade && (
                                    <span className="text-xs text-accent">
                                      📍 {card.spatial_tag.facade.toUpperCase()}
                                      {card.spatial_tag.level ? ` / ${card.spatial_tag.level.toUpperCase()}` : ''}
                                    </span>
                                  )}
                                  {photoCount > 0 && (
                                    <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                                      <Camera className="h-3 w-3" />
                                      {photoCount}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <p className="text-xs font-medium">
                                  £{card.estimated_cost?.low || 0}-{card.estimated_cost?.high || 0}
                                </p>
                                <button 
                                  type="button"
                                  onClick={() => removeFixCard(card.id)}
                                  className="text-destructive hover:text-destructive/80"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer with Budget Summary */}
        <div className="border-t pt-4 mt-auto">
          <div className="flex items-center justify-between">
            <div className="flex gap-4 items-center">
              {step === 'inspect' && (
                <>
                  {/* Severity counts */}
                  {Object.entries(SEVERITY_CONFIG).map(([sev, cfg]) => (
                    <div key={sev} className="flex items-center gap-1">
                      <div className={`w-3 h-3 rounded-full ${cfg.bg}`} />
                      <span className="text-sm">{fixCards.filter(c => c.severity === sev).length}</span>
                    </div>
                  ))}
                  
                  <Separator orientation="vertical" className="h-6" />
                  
                  {/* Budget Summary */}
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      £{budgetSummary.total_low.toLocaleString()} - £{budgetSummary.total_high.toLocaleString()}
                    </span>
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-2">
              {step === 'inspect' && (
                <Button type="button" variant="outline" onClick={() => setStep('dna')} data-testid="back-btn">
                  Back to DNA
                </Button>
              )}
              <Button type="button" variant="outline" onClick={onClose} data-testid="cancel-btn">
                Cancel
              </Button>
              {step === 'inspect' && (
                <Button 
                  type="button"
                  onClick={finishInspection}
                  disabled={fixCards.length === 0}
                  data-testid="finish-btn"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Complete ({fixCards.length})
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
